import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, CheckCircle } from 'lucide-react';
import Logo from '../components/Logo';
import Header from '../components/Header';

const Agreement = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Header />

      <div className="pt-10 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-gray-900">Accueil</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-900">Conditions d'utilisation</span>
          </nav>

          {/* Header */}
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
              <FileText className="w-7 h-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Conditions d'utilisation</h1>
              <p className="text-gray-500">Dernière mise à jour : 10 février 2026</p>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
              <p className="text-gray-700 m-0">
                En utilisant les services d'alphaswapp, vous acceptez d'être lié par les présentes conditions d'utilisation. 
                Veuillez les lire attentivement avant d'utiliser notre plateforme.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Acceptation des conditions</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700">
                  En accédant à notre site web et en utilisant nos services, vous confirmez que vous avez lu, 
                  compris et accepté d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces 
                  conditions, vous ne devez pas utiliser nos services.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Description des services</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700 mb-4">alphaswapp fournit une plateforme d'échange de cryptomonnaies permettant :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>L'échange de cryptomonnaies entre elles</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>L'achat de cryptomonnaies avec des devises FIAT</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>La vente de cryptomonnaies contre des devises FIAT</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>L'accès à une API pour les développeurs</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Conditions d'éligibilité</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700 mb-4">Pour utiliser nos services, vous devez :</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></span>
                    <span>Avoir au moins 18 ans ou l'âge légal dans votre juridiction</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></span>
                    <span>Ne pas être résident d'un pays soumis à des sanctions</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></span>
                    <span>Fournir des informations exactes et à jour</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2"></span>
                    <span>Compléter la vérification d'identité (KYC) si nécessaire</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Règles de conduite</h2>
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <p className="text-gray-700 mb-4 font-medium">Il est strictement interdit de :</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2"></span>
                    <span>Utiliser nos services pour des activités illégales ou frauduleuses</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2"></span>
                    <span>Blanchir de l'argent ou financer le terrorisme</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2"></span>
                    <span>Fournir de fausses informations ou documents</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2"></span>
                    <span>Tenter de pirater ou perturber nos systèmes</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Frais et commissions</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700">
                  Les frais applicables à chaque transaction sont clairement indiqués avant la confirmation de 
                  l'échange. alphaswapp se réserve le droit de modifier ses frais à tout moment, avec notification 
                  préalable aux utilisateurs. Les frais actuels varient de 0.3% à 0.5% selon le volume d'échange.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Limitation de responsabilité</h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-700">
                  alphaswapp ne peut être tenu responsable des pertes résultant de la volatilité du marché des 
                  cryptomonnaies, des erreurs d'adresse de portefeuille commises par l'utilisateur, ou des 
                  interruptions de service indépendantes de notre volonté.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Contact</h2>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
                <p className="text-gray-700 mb-4">
                  Pour toute question concernant ces conditions d'utilisation :
                </p>
                <div className="space-y-2">
                  <p className="text-gray-700"><strong>Email :</strong> legal@alphaswapp.com</p>
                  <p className="text-gray-700"><strong>Support :</strong> support@alphaswapp.com</p>
                </div>
              </div>
            </section>
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-8 border-t border-gray-200">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Retour à l'accueil</span>
            </Link>
            <div className="flex space-x-4">
              <Link to="/privacy" className="text-blue-600 hover:underline">Confidentialité</Link>
              <Link to="/cookies" className="text-blue-600 hover:underline">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agreement;
